import React from 'react';
import { Grid } from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import FormikTextField from 'catalyst/core/form/TextField/variants/FormikTextField';
import Button from 'catalyst/core/Button';
import { useHistory } from 'react-router-dom';
import config from 'coeus.config';
import { qlikClient } from 'constants/environmental';
import FormikSelectFallBack from 'catalyst/core/form/Select/variants/FormikSelectFallBack';
import FormikSelect from 'catalyst/core/form/Select/variants/FormikSelect';
import * as Icon from 'react-feather';
import FormikCheckbox from 'catalyst/core/form/Checkbox/variants/FormikCheckbox';

const IconsList = Object.keys(Icon).sort();
const icons = IconsList.map((icon) => ({
    label: icon,
    value: icon,
    Icon: Icon[icon],
}));

const SAAS = config.saas_client === qlikClient.SAAS;
const VALPAK_SSO = config?.page?.valpak_sso?.enabled;
const PUBLIC_ACCESS = config?.feature?.public_access?.enabled;


const validationSchema = {
    title: yup
        .string('Enter Page Title')
        .min(2, 'Name should be of minimum 2 characters length')
        .required('Title is required'),
    qsApp: yup
        .string()
        .required('A Default Qlik Sense App is Required')
        .nullable(),
};

const InputContainer = ({ children }) => {
    return (
        <Grid item xs={12} sm={4} style={{ marginBottom: '10px' }}>
            {children}
        </Grid>
    );
};

const PageForm = ({
    initialValues,
    updateForm,
    spaces,
    qsApps,
    setSpace,
    masterApps,
    pages,
    submitText,
  publicAccess
}) => {
    const history = useHistory();

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                updateForm(values);
            }}
            validationSchema={yup.object(validationSchema)}
        >
            {({ submitForm, isSubmitting, errors, touched, values }) => {
                const submit = (event) => {
                    event.preventDefault();
                    submitForm();
                };

                return (
                    <>
                        <Grid container direction="row" spacing={1}>
                            <InputContainer>
                                <FormikTextField
                                    id="title"
                                    label="Title"
                                    name="title"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    error={errors.title}
                                    touched={touched.title}
                                    helperText={touched.title && errors.title}
                                />
                            </InputContainer>
                            {SAAS && (
                                <InputContainer>
                                    <FormikSelectFallBack
                                        list={spaces}
                                        id="space"
                                        label="Qlik Saas Space"
                                        name="space"
                                        error={errors.spaces}
                                        touched={touched.spaces}
                                        helperText={
                                            touched.space && errors.spaces
                                        }
                                        handleChange={setSpace}
                                    />
                                </InputContainer>
                            )}
                            <InputContainer>
                                <FormikSelectFallBack
                                    list={qsApps}
                                    id="qsApp"
                                    label="Qlik Sense App"
                                    name="qsApp"
                                    error={errors.qsApp}
                                    touched={touched.qsApp}
                                    helperText={touched.qsApp && errors.qsApp}
                                />
                            </InputContainer>
                            {!SAAS && (
                                <InputContainer>
                                    <FormikSelectFallBack
                                        list={masterApps}
                                        id="masterApp"
                                        label="Dynamic Application Generation Master App"
                                        name="masterApp"
                                        error={errors.masterApp}
                                        touched={touched.masterApp}
                                        helperText={
                                            touched.masterApp &&
                                            errors.masterApp
                                        }
                                    />
                                </InputContainer>
                            )}

                            <InputContainer>
                                <FormikSelect
                                    id="status"
                                    label="Status"
                                    name="status"
                                    items={[
                                        {
                                            value: 'draft',
                                            label: 'Draft',
                                        },
                                        {
                                            value: 'published',
                                            label: 'Published',
                                        },
                                    ]}
                                    fullWidth
                                    error={errors.status}
                                    touched={touched.status}
                                    helperText={touched.status && errors.status}
                                />
                            </InputContainer>
                            <InputContainer>
                                <FormikSelectFallBack
                                    list={pages}
                                    id="parent"
                                    label="Parent"
                                    name="parent"
                                    error={errors.parent}
                                    touched={touched.parent}
                                    helperText={touched.parent && errors.parent}
                                />
                            </InputContainer>
                            <InputContainer>
                                <FormikSelect
                                    id="menu"
                                    label="Menu Visibility"
                                    name="menu"
                                    items={[
                                        {
                                            value: 'yes',
                                            label: 'show',
                                        },
                                        {
                                            value: 'no',
                                            label: 'Hide',
                                        },
                                    ]}
                                    fullWidth
                                    error={errors.menu}
                                    touched={touched.menu}
                                    helperText={touched.menu && errors.menu}
                                />
                            </InputContainer>
                            <InputContainer>
                                <FormikSelect
                                    id="icon"
                                    label="Page Icon"
                                    name="icon"
                                    items={icons}
                                    fullWidth
                                    error={errors.icon}
                                    touched={touched.icon}
                                    helperText={touched.icon && errors.icon}
                                />
                            </InputContainer>
                            <InputContainer>
                                <FormikTextField
                                    id="sortOrder"
                                    label="Menu Sort Order"
                                    name="sortOrder"
                                    type="number"
                                    inputProps={{ min: 0, max: 1000 }}
                                    variant="outlined"
                                    fullWidth
                                    error={errors.sortOrder}
                                    touched={touched.sortOrder}
                                    helperText={
                                        touched.sortOrder && errors.sortOrder
                                    }
                                />
                            </InputContainer>
                            {VALPAK_SSO && (
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    style={{ paddingTop: '25px' }}
                                >
                                    <FormikCheckbox
                                        id="valpakSSO"
                                        name="valpakSSO"
                                        label="Survey Single Sign On"
                                        helperText="Valpak Only"
                                    />
                                </Grid>
                            )}

                            {
                                PUBLIC_ACCESS && (
                                  <Grid
                                  item
                                  xs={12}
                                  sm={4}
                                  style={{ paddingTop: '25px' }}
                                >
                                    <FormikCheckbox
                                      id="publicAccess"
                                      name="publicAccess"
                                      label="Public Access Page"
                                      helperText="Page visible to none-logged in user"
                                    />
                                </Grid>
                                )}
                            <Grid item sm={12}>
                                <FormikTextField
                                    id="description"
                                    label="Description"
                                    name="description"
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    multiline={true}
                                    minRows={2}
                                    maxRows={4}
                                    error={errors.sortOrder}
                                    touched={touched.sortOrder}
                                    helperText={
                                        touched.sortOrder && errors.sortOrder
                                    }
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            spacing={1}
                            justifyContent={'space-between'}
                            style={{ marginBottom: '10px' }}
                        >
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    onClick={submit}
                                >
                                    {submitText}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => history.goBack()}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                );
            }}
        </Formik>
    );
};

export default PageForm;
